/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'

type UnderAgeErrorProps = {
  /** Return to the Welcome step (step 0), keeping the user's prior selections. */
  onBack?: () => void
  /** Hide the "HEADS UP!" title — used when rendered inside a card that already
   *  carries the invite header. Defaults to showing it. */
  hideHeader?: boolean
}

/**
 * UnderAgeError component for the underage invitation flow
 * Displays a message when a user is under 13 years old
 *
 * @returns {JSX.Element} The rendered component
 */
const UnderAgeError = ({ onBack, hideHeader = false }: UnderAgeErrorProps) => {
  // InviteV2 renders this inside its own card and passes hideHeader; that
  // surface keeps the original left-aligned copy and its own spacing.
  if (hideHeader) {
    return (
      <div css={[containerStyle, noTopPadStyle]}>
        <div>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_0}
          >
            Seems like you&apos;re not 13 yet. <br />
            <br /> Please have a parent or guardian create your{' '}
            {t('common:appName', {})} account. Thanks!
          </Text>
        </div>
        {onBack && (
          <div css={legacyFooterStyle}>
            <div css={actionColumnStyle}>
              <Button text='Back' variant='secondary' onClick={onBack} />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div css={centeredContainerStyle}>
      <Text
        variant='heading2'
        weight='semiBold'
        color={colors.NEUTRAL_0}
        style={'text-align: center;'}
      >
        {capitalizeWords('Heads Up!')}
      </Text>
      <Text
        variant='paragraphMedium'
        weight='regular'
        color={colors.NEUTRAL_50}
      >
        Seems like you&apos;re not 13 yet. Please have a parent or guardian
        create your {t('common:appName', {})} account. Thanks!
      </Text>
      {onBack && (
        <div css={footerStyle}>
          <div css={actionColumnStyle}>
            <Button text='Back' variant='secondary' onClick={onBack} />
          </div>
        </div>
      )}
    </div>
  )
}

export default UnderAgeError

// ---- styled ----
/*
 * The title centers; the message and the Back button span the column, so the
 * body copy reads from the same left edge as the rest of the flow's text.
 */
const centeredContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const footerStyle = css`
  display: flex;
  gap: 8px;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const actionColumnStyle = css`
  display: flex;
  flex: 1;
  min-width: 0;
`

// ---- InviteV2 (hideHeader) — original layout, unchanged ----
const containerStyle = css`
  width: 100%;
`

// When rendered inside a card that already shows the invite header, drop the
// top padding so the message sits directly under the card divider.
const noTopPadStyle = css`
  padding-top: 0;
`

const legacyFooterStyle = css`
  display: flex;
  width: 100%;
  margin-top: 32px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`
